import { ThunkDispatch } from 'redux-thunk'

import { PAYMENT_PROVIDERS } from 'App/constants'
import { operationProcessAdyenCheckoutResponse } from 'features/adyen/duck/operations'
import {
  CHECKOUT_REDIRECT_ACTIONS,
  CheckoutSuccessAction,
  CHECKOUT_SUCCESS_ADYEN_ACTIONS,
  CHECKOUT_SUCCESS_CYBERSOURCE_ACTIONS,
  PaymentMethod,
} from 'features/checkout/constants'
import { postProceedSuccess, updateRupayData, resetRupayData } from 'features/checkout/duck/actions'
import { AuthError } from 'features/checkout/duck/operations/postProceed'
import {
  PostProceedResponse,
  RupayPostProceedResponse,
} from 'features/checkout/duck/operations/postProceed/prepareProceedBody/paymentProviders'
import { operationProcessCreditGuardCheckoutResponse } from 'features/creditguard/duck/operations'
import { operationProcessCybersourceCheckoutResponse } from 'features/cybersource/duck/operations'
import { operationProcessRazorpayCheckoutResponse } from 'features/razorpay/duck/operations'
import { RootState } from 'redux/types'

interface HandleProceedResponseArgs {
  response: PostProceedResponse
  selectedPaymentMethod: PaymentMethod
  dispatch: ThunkDispatch<RootState, undefined, any>
}

export function handleProceedResponse({
  response,
  selectedPaymentMethod,
  dispatch,
}: HandleProceedResponseArgs): void {
  const { successAction, redirectURL, ...proceedResponse } = response

  const isAdyenCheckoutRedirect =
    selectedPaymentMethod === PaymentMethod.Adyen &&
    successAction === CheckoutSuccessAction.ExternalRedirect
  const isAdyenCheckoutSuccessAction = CHECKOUT_SUCCESS_ADYEN_ACTIONS.includes(successAction)

  if (isAdyenCheckoutRedirect || isAdyenCheckoutSuccessAction) {
    dispatch(operationProcessAdyenCheckoutResponse(proceedResponse))
    return
  }

  const isCybersourceCheckoutSuccessAction =
    CHECKOUT_SUCCESS_CYBERSOURCE_ACTIONS.includes(successAction)

  if (isCybersourceCheckoutSuccessAction) {
    dispatch(operationProcessCybersourceCheckoutResponse(proceedResponse))
  }

  if (successAction === CheckoutSuccessAction.ExternalPostRedirect) {
    const rupayProceedResponse = proceedResponse as RupayPostProceedResponse
    const rupayAccessCode = rupayProceedResponse.pageAttributes?.rupayAccessCode
    const { submitForm } = rupayProceedResponse

    if (!redirectURL || !rupayAccessCode || !submitForm) {
      dispatch(resetRupayData())
      const rupayError: AuthError = new Error('Rupay missing crucial data')
      rupayError.code = PaymentMethod.Rupay
      throw rupayError
    } else {
      dispatch(
        updateRupayData({
          redirectURL,
          rupayAccessCode,
          submitForm,
        }),
      )
    }
  }

  if (
    successAction === CheckoutSuccessAction.ExternalPaymentWidget &&
    proceedResponse?.paymentProvider === PAYMENT_PROVIDERS.RAZORPAY
  ) {
    dispatch(operationProcessRazorpayCheckoutResponse(proceedResponse))
    return
  }

  if (
    successAction === CheckoutSuccessAction.ExternalPaymentWidget &&
    proceedResponse?.paymentProvider === PAYMENT_PROVIDERS.CREDITGUARD
  ) {
    dispatch(operationProcessCreditGuardCheckoutResponse(proceedResponse))
    return
  }

  if (CHECKOUT_REDIRECT_ACTIONS.includes(successAction) && redirectURL) {
    window.location.assign(redirectURL)
    dispatch(postProceedSuccess())
  }
}
